import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import AirIcon from '@mui/icons-material/Air';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import DashboardMetricCard from './DashboardMetricCard';

const tomorrow = { a: "Duz3", b: "59QJsgMP53YVt", c: "xLaM6AZVYaIgLZf" };
const TOMORROW_API_KEY = tomorrow.c + tomorrow.b + tomorrow.a;

const msToMph = (m) => m * 2.23694;
const cToF = (c) => (c * 9 / 5) + 32;
const milesToKm = (miles) => miles * 1.60934;

function formatTemperature(celsius, unit = 'F') {
  return unit === 'C'
    ? `${Math.round(celsius)}°C`
    : `${Math.round(cToF(celsius))}°F`;
}

function formatSpeed(mph, unit = 'mi') {
  return unit === 'km'
    ? `${Math.round(milesToKm(mph))} km/h`
    : `${Math.round(mph)} mph`;
}

function formatDistance(miles, unit = 'mi') {
  return unit === 'km'
    ? `${milesToKm(miles).toFixed(1)} km`
    : `${miles.toFixed(1)} mi`;
}

function formatObservationTime(observedAt) {
  if (!observedAt) return 'Unknown update time';
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  }).format(observedAt);
}

function formatAge(ageMinutes) {
  if (!Number.isFinite(ageMinutes)) return 'age unknown';
  if (ageMinutes < 1) return 'just now';
  if (ageMinutes === 1) return '1 minute ago';
  if (ageMinutes < 60) return `${ageMinutes} minutes ago`;
  const hours = Math.floor(ageMinutes / 60);
  const minutes = ageMinutes % 60;
  return minutes
    ? `${hours} hr ${minutes} min ago`
    : `${hours} hr ago`;
}

function windSeverity(mph, maxWind) {
  if (!isFinite(maxWind) || maxWind <= 0) return 'advisory';
  if (mph > maxWind) return 'no-go';
  if (mph > maxWind * 0.75) return 'caution';
  return 'go';
}

function normalizeDroneSpecs(drone) {
  if (drone && typeof drone === 'object') {
    const maxWind = typeof drone.maxWindTolerance === 'number' ? drone.maxWindTolerance :
                    typeof drone.maxWind === 'number' ? drone.maxWind : 22;
    const canFlyInRain = drone.canFlyInRain === true || drone.canFlyInRain === 'true';
    return {
      name: drone.name || 'Custom Drone',
      maxWind,
      canFlyInRain
    };
  }
  return { name: 'DJI Phantom', maxWind: 22, canFlyInRain: false };
}

const WeatherCardDashboard = ({ lat, lon, drone, refreshTrigger, onUpdate, preferences = {} }) => {
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState('unknown');
  const [weatherData, setWeatherData] = useState(null);

  const currentSpecs = useMemo(() => normalizeDroneSpecs(drone), [drone]);
  const temperatureUnit = preferences.temperatureUnit || 'F';
  const distanceUnit = preferences.distanceUnit || 'mi';

  useEffect(() => {
    const fetchWeather = async () => {
      if (!lat || !lon) {
        setSeverity('advisory');
        onUpdate && onUpdate('advisory', ['No location set']);
        setLoading(false);
        return;
      }

      // Only show loading on first load, not on refreshes
      if (!weatherData) {
        setLoading(true);
      }
      try {
        const res = await fetch(
          `https://api.tomorrow.io/v4/weather/realtime?location=${lat},${lon}&apikey=${TOMORROW_API_KEY}`
        );
        const data = await res.json();

        if (!res.ok || !data.data) throw new Error('Tomorrow API error');

        const c = data.data.values;
        const observedAt = data.data.time ? new Date(data.data.time) : null;
        const observedAtMs = observedAt?.getTime();
        const ageMinutes = Number.isFinite(observedAtMs)
          ? Math.max(0, Math.round((Date.now() - observedAtMs) / 60000))
          : null;
        const windMph = msToMph(c.windSpeed || 0);
        const hasRealGust = Object.prototype.hasOwnProperty.call(c, 'windGust')
          && c.windGust !== null
          && c.windGust !== undefined
          && Number.isFinite(Number(c.windGust));
        const gustMph = hasRealGust ? msToMph(Number(c.windGust)) : null;
        const visMiles = (c.visibility || 0) * 0.621371;

        // Evaluate severity
        const issues = [];
        const wSev = windSeverity(windMph, currentSpecs.maxWind);
        const gSev = hasRealGust ? windSeverity(gustMph, currentSpecs.maxWind) : 'go';

        if (wSev === 'no-go') issues.push(`Sustained wind ${Math.round(windMph)} mph exceeds ${currentSpecs.name} limit of ${currentSpecs.maxWind} mph`);
        else if (wSev === 'caution') issues.push(`Sustained wind ${Math.round(windMph)} mph is near ${currentSpecs.name} limit of ${currentSpecs.maxWind} mph`);

        if (hasRealGust && gSev === 'no-go') issues.push(`Wind gusts ${Math.round(gustMph)} mph exceed ${currentSpecs.name} limit of ${currentSpecs.maxWind} mph`);
        else if (hasRealGust && gSev === 'caution') issues.push(`Wind gusts ${Math.round(gustMph)} mph are near ${currentSpecs.name} limit of ${currentSpecs.maxWind} mph`);

        if (visMiles < 3) issues.push(`Visibility ${visMiles.toFixed(1)} mi is below the 3 mi planning threshold`);
        if (c.humidity > 95) issues.push(`Humidity ${Math.round(c.humidity)}% may indicate fog or moisture risk`);
        if (Number.isFinite(ageMinutes) && ageMinutes >= 15) {
          issues.push(ageMinutes > 60 ? 'Weather data is stale' : 'Weather data may be stale');
        }

        let sev = 'go';
        if (wSev === 'no-go' || gSev === 'no-go' || visMiles < 3) sev = 'no-go';
        else if (issues.length > 0) sev = 'caution';
        if (Number.isFinite(ageMinutes) && ageMinutes > 60 && sev === 'go') sev = 'advisory';

        setWeatherData({
          temperature: c.temperature,
          windSpeed: windMph,
          windGust: gustMph,
          hasRealGust,
          visibility: visMiles,
          humidity: c.humidity,
          cloudCover: c.cloudCover,
          weatherCode: c.weatherCode,
          observedAt,
          ageMinutes,
          issues,
          provider: 'weather data',
          stationId: null
        });

        setSeverity(sev);
        onUpdate && onUpdate(sev, issues);
        setLoading(false);
      } catch (err) {
        console.error('Weather fetch failed:', err);
        setSeverity('advisory');
        onUpdate && onUpdate('advisory', ['Weather data unavailable']);
        setLoading(false);
      }
    };

    fetchWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, refreshTrigger]);

  const primaryMetric = weatherData ? {
    value: formatTemperature(weatherData.temperature, temperatureUnit),
    label: 'Current Temperature'
  } : null;

  const primaryIssue = weatherData?.issues?.[0] || '';

  const secondaryMetrics = weatherData ? [
    { label: 'Wind', value: formatSpeed(weatherData.windSpeed, distanceUnit), highlight: windSeverity(weatherData.windSpeed, currentSpecs.maxWind) !== 'go' },
    {
      label: 'Gusts',
      value: weatherData.hasRealGust
        ? `${formatSpeed(weatherData.windGust, distanceUnit)}, updated ${formatAge(weatherData.ageMinutes)}`
        : 'not reported',
      highlight: weatherData.hasRealGust && windSeverity(weatherData.windGust, currentSpecs.maxWind) !== 'go'
    },
    { label: 'Visibility', value: formatDistance(weatherData.visibility, distanceUnit) },
    { label: 'Humidity', value: `${Math.round(weatherData.humidity)}%` }
  ] : [];

  const statusMessage = severity === 'go'
    ? 'Conditions optimal for flight'
    : severity === 'advisory'
    ? 'Weather data may be stale'
    : severity === 'caution'
    ? (Number.isFinite(weatherData?.ageMinutes) && weatherData.ageMinutes >= 15
      ? 'Weather data may be stale'
      : 'Marginal conditions - proceed with caution')
    : severity === 'no-go'
    ? (primaryIssue || 'Unsafe weather conditions')
    : 'Checking weather...';

  return (
    <DashboardMetricCard
      title="Weather"
      icon={CloudIcon}
      status={severity}
      primaryMetric={primaryMetric}
      secondaryMetrics={secondaryMetrics}
      statusMessage={statusMessage}
      loading={loading}
      minHeight={112}
    >
      {weatherData && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Detailed Conditions
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.72, mb: 1 }}>
            Updated {formatObservationTime(weatherData.observedAt)}
            {weatherData.stationId ? ` from ${weatherData.stationId}` : ''}
            {`, ${formatAge(weatherData.ageMinutes)}`}
          </Typography>
          {weatherData.issues?.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {weatherData.issues.slice(0, 3).map((issue, idx) => (
                <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'error.light', fontWeight: 700 }}>
                  {issue}
                </Typography>
              ))}
            </Box>
          )}
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ThermostatIcon fontSize="small" />
                <Typography variant="body2">
                  {formatTemperature(weatherData.temperature, temperatureUnit)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <AirIcon fontSize="small" />
                <Typography variant="body2">
                  {formatSpeed(weatherData.windSpeed, distanceUnit)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CloudIcon fontSize="small" />
                <Typography variant="body2">
                  {weatherData.cloudCover}% clouds
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <VisibilityIcon fontSize="small" />
                <Typography variant="body2">
                  {formatDistance(weatherData.visibility, distanceUnit)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box mt={2}>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>
              {weatherData.hasRealGust
                ? `Gusts ${formatSpeed(weatherData.windGust, distanceUnit)}, updated ${formatAge(weatherData.ageMinutes)}`
                : 'Gusts not reported'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Drone: {currentSpecs.name} (Max wind: {formatSpeed(currentSpecs.maxWind, distanceUnit)})
            </Typography>
          </Box>
        </Box>
      )}
    </DashboardMetricCard>
  );
};

export default WeatherCardDashboard;
